/**
 * AN OPEN CALL FOR A TEST (v9.5.3) - the call while it waits, as a document
 * the GM's screen keeps (`testCalls/<id>`, GM-only), so a reload keeps it
 * and a secret result may sit in it.
 *
 * `prompted` says, per person asked, which accounts were prompted (several
 * when more than one seat acted as them this session); an empty list means
 * the GM rolls them at once. `waiting` is who has not settled; `results`
 * what has been rolled so far. `closedAt` is stamped THE MOMENT the last
 * person settles, before the group line and the riders fire, so a screen
 * that reloads after it fires nothing twice.
 *
 * Pure, zero React dependency.
 */
import { readTestCall, type CalledResult, type TestCall } from './callForTest';
import { readTestAnswer } from './testAnswer';

export interface OpenCall {
  id: string;
  call: TestCall;
  createdAt: number;
  /** combatantId -> the accounts prompted; [] = rolled on the GM's screen. */
  prompted: Record<string, string[]>;
  waiting: string[];
  results: CalledResult[];
  closedAt: number | null;
}

export function openCallOf(id: string, call: TestCall, prompted: Record<string, string[]>, now: number): OpenCall {
  const waiting = call.who.filter((cid) => cid in prompted);
  return { id, call, createdAt: now, prompted, waiting, results: [], closedAt: null };
}

/**
 * One person settles: rolled (`result`) or let go (null). Null when they
 * were settled already - the first settlement stands.
 */
export function settleOne(open: OpenCall, combatantId: string, result: CalledResult | null): OpenCall | null {
  if (!open.waiting.includes(combatantId)) return null;
  return {
    ...open,
    waiting: open.waiting.filter((cid) => cid !== combatantId),
    results: result ? [...open.results, result] : open.results,
  };
}

/** Nobody is waited on any more. */
export const isDone = (open: OpenCall): boolean => open.waiting.length === 0;

/** Stamp the close; the same call when it is closed already. */
export function closeCall(open: OpenCall, now: number): OpenCall {
  return open.closedAt === null ? { ...open, closedAt: now } : open;
}

const ids = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string' && v !== '') : [];

function readPrompted(raw: unknown): Record<string, string[]> {
  const stored = (raw ?? {}) as Record<string, unknown>;
  return Object.fromEntries(Object.entries(stored).map(([cid, uids]) => [cid, ids(uids)]));
}

function readResults(raw: unknown): CalledResult[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r) => {
    const cid = (r as { combatantId?: unknown })?.combatantId;
    const read = typeof cid === 'string' ? readTestAnswer({ callId: 'x', combatantId: cid, result: r }) : null;
    return read ? [read.result] : [];
  });
}

const stamp = (raw: unknown): number | null => (typeof raw === 'number' && Number.isFinite(raw) ? raw : null);

/** A stored open call, validated; null when its call cannot be read. */
export function readOpenCall(id: string, raw: unknown): OpenCall | null {
  const stored = (raw ?? {}) as Record<string, unknown>;
  const call = readTestCall(stored.call);
  if (!call) return null;
  const prompted = readPrompted(stored.prompted);
  return {
    id,
    call,
    createdAt: stamp(stored.createdAt) ?? 0,
    prompted,
    waiting: ids(stored.waiting).filter((cid) => call.who.includes(cid)),
    results: readResults(stored.results),
    closedAt: stamp(stored.closedAt),
  };
}
